/**
 * NominaBatchClassifyDialog — Gestión Documental Fase E (lote, mb45.com, 2026-10)
 * Un email de la asesoría suele traer varios documentos del MISMO trabajador
 * y la MISMA fecha (alta, contrato, nómina...). Este diálogo analiza el email
 * UNA sola vez, propone empleado/periodo/fecha compartidos y el tipo de cada
 * adjunto (por nombre de archivo), y archiva todo junto en Drive tras revisión.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, FileText, ExternalLink, FolderArchive, UserCheck, AlertTriangle, BrainCircuit, Layers } from 'lucide-react';
import { API_URL } from '../config';

const API = API_URL;

const TIPOS = [
  { value: 'nomina', label: 'Nómina' },
  { value: 'contrato', label: 'Contrato' },
  { value: 'alta_ss', label: 'Alta SS' },
  { value: 'baja_ss', label: 'Baja SS / Extinción' },
  { value: 'otro', label: 'Otro' },
];

export const NominaBatchClassifyDialog = ({ open, onOpenChange, token, emailId }) => {
  const [step, setStep] = useState('loading'); // loading | review | done
  const [shared, setShared] = useState({ empleado_nombre: '', empleado_user_id: null, periodo: '', fecha_documento: '' });
  const [candidatos, setCandidatos] = useState([]);
  const [detectedName, setDetectedName] = useState('');
  const [remitente, setRemitente] = useState('');
  const [items, setItems] = useState([]); // [{filename, tipo_documento, importe, incluir}]
  const [confirming, setConfirming] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [aliasPrompted, setAliasPrompted] = useState(false);
  const [savingAlias, setSavingAlias] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const loadAnalysis = async () => {
    setStep('loading');
    setResultado(null);
    setAliasPrompted(false);
    try {
      const res = await axios.post(
        `${API}/documentos-archivados/classify-nomina-email`,
        { email_id: emailId },
        { headers, timeout: 120000 }
      );
      const d = res.data;
      const mejorCandidato = (d.empleado_candidatos || [])[0];
      // FIX (mismo bug que NominaClassifyDialog): usar el nombre del
      // candidato de alta confianza (alias ya aprendido / apodo, score >=
      // 0.75) en vez de siempre preferir el nombre legal detectado.
      const nombreResuelto = (mejorCandidato && mejorCandidato.score >= 0.75)
        ? mejorCandidato.nombre
        : (d.empleado_nombre_detectado || mejorCandidato?.nombre || '');
      setShared({
        empleado_nombre: nombreResuelto,
        empleado_user_id: mejorCandidato?.score >= 0.75 ? mejorCandidato.user_id : null,
        periodo: d.periodo || '',
        fecha_documento: d.fecha_documento || '',
      });
      setCandidatos(d.empleado_candidatos || []);
      setDetectedName(d.empleado_nombre_detectado || '');
      setRemitente(d.remitente || '');
      setItems(
        (d.attachments || []).map((a) => ({
          filename: a.filename,
          tipo_documento: a.tipo_documento_sugerido || 'otro',
          importe: d.importe ?? '',
          incluir: true,
        }))
      );
      setStep('review');
    } catch (e) {
      const timedOut = e.code === 'ECONNABORTED';
      setErrorMsg(timedOut ? 'El análisis tardó demasiado tiempo. Inténtalo de nuevo.' : (e.response?.data?.detail || 'No se pudo analizar el email'));
      setStep('error');
    }
  };

  useEffect(() => {
    if (!open || !emailId) return;
    loadAnalysis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, emailId]);

  const selectCandidato = (c) => {
    setShared({ ...shared, empleado_nombre: c.nombre, empleado_user_id: c.user_id });
  };

  const updateItem = (idx, patch) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const seleccionados = items.filter((it) => it.incluir);

  const handleConfirm = async () => {
    if (seleccionados.length === 0) {
      toast.error('Selecciona al menos un documento para archivar');
      return;
    }
    setConfirming(true);
    try {
      const res = await axios.post(
        `${API}/documentos-archivados/confirm-classify-nomina-batch`,
        {
          email_id: emailId,
          empleado_nombre: shared.empleado_nombre || null,
          empleado_user_id: shared.empleado_user_id || null,
          periodo: shared.periodo || null,
          fecha_documento: shared.fecha_documento || null,
          items: seleccionados.map((it) => ({
            filename: it.filename,
            tipo_documento: it.tipo_documento,
            importe: it.importe === '' ? null : parseFloat(it.importe),
          })),
        },
        { headers }
      );
      setResultado(res.data);
      setStep('done');
      const n = (res.data.documentos || []).length;
      if ((res.data.errores || []).length > 0) {
        toast.warning(`${n} archivados, ${res.data.errores.length} con error`);
      } else {
        toast.success(`${n} documentos archivados en Google Drive`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudieron archivar los documentos');
    } finally {
      setConfirming(false);
    }
  };

  const normalizar = (s) => (s || '').toLowerCase().trim();
  // No volver a preguntar si el nombre final ya coincide con un candidato de
  // alta confianza (score >= 0.97 = alias ya aprendido anteriormente).
  const yaEsAliasConocido = candidatos[0] && candidatos[0].score >= 0.97 &&
    normalizar(candidatos[0].nombre) === normalizar(shared.empleado_nombre);
  const mostrarPromptAlias =
    step === 'done' && !aliasPrompted && detectedName && shared.empleado_nombre &&
    normalizar(detectedName) !== normalizar(shared.empleado_nombre) && !yaEsAliasConocido;

  const handleSaveAlias = async (recordar) => {
    setAliasPrompted(true);
    if (!recordar) return;
    setSavingAlias(true);
    try {
      await axios.post(
        `${API}/documentos-archivados/empleado-alias`,
        { alias: shared.empleado_nombre, nombre_legal: detectedName, user_id: shared.empleado_user_id || null },
        { headers }
      );
      toast.success(`Recordado: "${detectedName}" = "${shared.empleado_nombre}"`);
    } catch (e) {
      toast.error('No se pudo guardar el alias');
    } finally {
      setSavingAlias(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-slate-900 border-slate-700 text-white max-h-[88vh] flex flex-col" data-testid="nomina-batch-dialog">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-amber-400" />
            Clasificar todos los documentos de este email
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {step === 'review' ? `${items.length} documento${items.length !== 1 ? 's' : ''} encontrados` : 'mb45.com'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 pr-1">
          {step === 'loading' && (
            <div className="flex flex-col items-center justify-center py-10 gap-3 text-slate-400" data-testid="nomina-batch-loading">
              <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
              <p className="text-sm">Analizando todos los documentos del email...</p>
            </div>
          )}

          {step === 'error' && (
            <div className="flex flex-col items-center justify-center py-10 gap-3 text-center" data-testid="nomina-batch-error">
              <AlertTriangle className="h-8 w-8 text-rose-400" />
              <p className="text-sm text-rose-300">{errorMsg}</p>
              <Button size="sm" onClick={loadAnalysis} className="bg-amber-600 hover:bg-amber-500" data-testid="nomina-batch-retry-btn">
                Reintentar
              </Button>
            </div>
          )}

          {step === 'review' && (
            <>
              <p className="text-xs text-slate-500">Remitente: {remitente || 'desconocido'}</p>

              {candidatos.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-slate-300">Empleado sugerido (fuzzy-match)</Label>
                  <div className="flex flex-wrap gap-2">
                    {candidatos.map((c, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => selectCandidato(c)}
                        className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition-colors ${
                          shared.empleado_nombre === c.nombre
                            ? 'bg-amber-500/20 border-amber-500 text-amber-300'
                            : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-500'
                        }`}
                        data-testid={`nomina-batch-candidato-${i}`}
                      >
                        <UserCheck className="h-3 w-3" />
                        {c.nombre}
                        <span className="opacity-60">{Math.round(c.score * 100)}%</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-slate-300">Nombre del empleado (se aplica a todos los documentos)</Label>
                {(candidatos.length === 0 || (candidatos[0]?.score ?? 0) < 0.4) && (
                  <div
                    className="flex items-center gap-1.5 text-xs text-orange-300 bg-orange-500/10 border border-orange-500/30 rounded-md px-2.5 py-1.5"
                    data-testid="nomina-batch-no-match-warning"
                  >
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    Ningún empleado conocido con alta similitud — revisa y escribe el nombre manualmente
                  </div>
                )}
                <Input
                  value={shared.empleado_nombre}
                  onChange={(e) => setShared({ ...shared, empleado_nombre: e.target.value, empleado_user_id: null })}
                  placeholder="Nombre y apellidos"
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="nomina-batch-empleado-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-slate-300">Periodo</Label>
                  <Input
                    type="month"
                    value={shared.periodo}
                    onChange={(e) => setShared({ ...shared, periodo: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="nomina-batch-periodo-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Fecha</Label>
                  <Input
                    type="date"
                    value={shared.fecha_documento}
                    onChange={(e) => setShared({ ...shared, fecha_documento: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="nomina-batch-fecha-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Documentos a archivar</Label>
                <div className="space-y-2">
                  {items.map((it, idx) => (
                    <div key={it.filename} className="flex items-center gap-2 bg-slate-800/60 border border-slate-700 rounded-lg px-2.5 py-2" data-testid={`nomina-batch-item-${idx}`}>
                      <Checkbox
                        checked={it.incluir}
                        onCheckedChange={(v) => updateItem(idx, { incluir: !!v })}
                        data-testid={`nomina-batch-item-check-${idx}`}
                      />
                      <FileText className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                      <span className="text-xs text-slate-300 truncate flex-1" title={it.filename}>{it.filename}</span>
                      <Select value={it.tipo_documento} onValueChange={(v) => updateItem(idx, { tipo_documento: v })} disabled={!it.incluir}>
                        <SelectTrigger className="bg-slate-900 border-slate-700 text-white h-8 w-[150px] shrink-0" data-testid={`nomina-batch-item-tipo-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700 text-white">
                          {TIPOS.map((t) => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {it.tipo_documento === 'nomina' && it.incluir && (
                        <Input
                          type="number"
                          step="0.01"
                          value={it.importe}
                          onChange={(e) => updateItem(idx, { importe: e.target.value })}
                          placeholder="Importe €"
                          className="bg-slate-900 border-slate-700 text-white h-8 w-[100px] shrink-0"
                          data-testid={`nomina-batch-item-importe-${idx}`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {step === 'done' && resultado && (
            <div className="space-y-3 py-2" data-testid="nomina-batch-done">
              {(resultado.documentos || []).map((doc) => (
                <div key={doc.id} className="flex items-center gap-2 text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 text-sm">
                  <FolderArchive className="h-4 w-4 shrink-0" />
                  <span className="flex-1 truncate">{doc.filename} — {doc.drive_folder_path}</span>
                  {doc.drive_web_view_link && (
                    <a href={doc.drive_web_view_link} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline shrink-0">
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
              ))}
              {(resultado.errores || []).map((err, i) => (
                <div key={i} className="flex items-center gap-2 text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2 text-sm">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span className="flex-1 truncate">{err.filename}: {err.error}</span>
                </div>
              ))}
              {mostrarPromptAlias && (
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3 space-y-2" data-testid="nomina-batch-alias-prompt">
                  <p className="text-sm text-cyan-200 flex items-start gap-1.5">
                    <BrainCircuit className="h-4 w-4 shrink-0 mt-0.5" />
                    ¿Recordar que "{detectedName}" = "{shared.empleado_nombre}" para que la próxima vez coincida automáticamente?
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      disabled={savingAlias}
                      onClick={() => handleSaveAlias(true)}
                      className="bg-cyan-600 hover:bg-cyan-500"
                      data-testid="nomina-batch-alias-remember-btn"
                    >
                      {savingAlias ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
                      Sí, recordar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSaveAlias(false)}
                      className="border-slate-600 text-slate-300"
                      data-testid="nomina-batch-alias-skip-btn"
                    >
                      No
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0">
          {step === 'review' && (
            <Button
              onClick={handleConfirm}
              disabled={confirming || seleccionados.length === 0}
              className="bg-amber-600 hover:bg-amber-500"
              data-testid="nomina-batch-confirm-btn"
            >
              {confirming ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FolderArchive className="h-4 w-4 mr-2" />}
              Confirmar y archivar {seleccionados.length} documento{seleccionados.length !== 1 ? 's' : ''}
            </Button>
          )}
          {step === 'done' && (
            <Button onClick={() => onOpenChange(false)} variant="outline" className="border-slate-600 text-slate-200" data-testid="nomina-batch-close-btn">
              Cerrar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NominaBatchClassifyDialog;
